import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Stack,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  collection,
  getDocs,
  getDoc,
  query,
  where,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { firestore } from "../firebase.config";
import PageNavBar from "../components/PageNavBar";
import { useUserContext } from "../context/UserContext";
import TenantBanner from "../components/TenantBanner";

const TimerManagement = () => {
  const [timerRecords, setTimerRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editForm, setEditForm] = useState({
    date: "",
    startTime: "",
    endTime: "",
    totalTimeBooked: "",
    taskDescription: "",
  });
  const [selectedTenant, setSelectedTenant] = useState("");
  const [tenantList, setTenantList] = useState([]);
  const [documentIdFilter, setDocumentIdFilter] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const {
    userRole: contextUserRole,
    tenantId: contextTenantId,
    selectedTenantId: contextSelectedTenantId,
  } = useUserContext();

  // Auto-hide success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage("");
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Initialize state from context and sessionStorage
  const [userRole, setUserRole] = useState("");
  const [tenantId, setTenantId] = useState("");

  // Sync context values with local state
  useEffect(() => {
    console.log("TimerManagement: Context values updated", {
      contextUserRole,
      contextTenantId,
      contextSelectedTenantId
    });
    
    // Get values from context first, then fallback to sessionStorage
    const role = contextUserRole || sessionStorage.getItem("userRole") || "";
    const tenant = contextTenantId || sessionStorage.getItem("tenantId") || "";
    const selectedTenantFromContext = contextSelectedTenantId || sessionStorage.getItem("selectedTenantId") || "";

    console.log("TimerManagement: Setting state values", {
      role,
      tenant,
      selectedTenantFromContext
    });

    setUserRole(role);
    setTenantId(tenant);
    
    // Set selectedTenant based on user role
    if (role === "SuperAdmin") {
      setSelectedTenant(selectedTenantFromContext);
    } else {
      setSelectedTenant(tenant);
    }
  }, [contextUserRole, contextTenantId, contextSelectedTenantId]);

  // Fallback: If context is not loaded after a delay, try to load from sessionStorage directly
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!userRole && !contextUserRole) {
        console.log("TimerManagement: Context not loaded, trying direct sessionStorage fallback");
        const fallbackRole = sessionStorage.getItem("userRole");
        const fallbackTenant = sessionStorage.getItem("tenantId");
        const fallbackSelectedTenant = sessionStorage.getItem("selectedTenantId");
        
        if (fallbackRole) {
          console.log("TimerManagement: Using fallback role", fallbackRole);
          setUserRole(fallbackRole);
        }
        
        if (fallbackTenant) {
          console.log("TimerManagement: Using fallback tenant", fallbackTenant);
          setTenantId(fallbackTenant);
        }
        
        if (fallbackSelectedTenant && fallbackRole === "SuperAdmin") {
          console.log("TimerManagement: Using fallback selectedTenant", fallbackSelectedTenant);
          setSelectedTenant(fallbackSelectedTenant);
        } else if (fallbackTenant) {
          setSelectedTenant(fallbackTenant);
        }
      }
    }, 1000); // Wait 1 second for context to load

    return () => clearTimeout(timer);
  }, [userRole, contextUserRole]);

  // Component mount effect - ensure we have the latest data
  useEffect(() => {
    console.log("TimerManagement: Component mounted, checking for data");
    
    // If we still don't have userRole after context sync, try to get it from sessionStorage
    if (!userRole) {
      const storedRole = sessionStorage.getItem("userRole");
      const storedTenant = sessionStorage.getItem("tenantId");
      const storedSelectedTenant = sessionStorage.getItem("selectedTenantId");
      
      if (storedRole) {
        console.log("TimerManagement: Component mount - setting role from sessionStorage", storedRole);
        setUserRole(storedRole);
      }
      
      if (storedTenant) {
        console.log("TimerManagement: Component mount - setting tenant from sessionStorage", storedTenant);
        setTenantId(storedTenant);
      }
      
      if (storedSelectedTenant && storedRole === "SuperAdmin") {
        console.log("TimerManagement: Component mount - setting selectedTenant from sessionStorage", storedSelectedTenant);
        setSelectedTenant(storedSelectedTenant);
      } else if (storedTenant) {
        setSelectedTenant(storedTenant);
      }
    }
  }, []); // Only run once on mount

  // Fetch tenants for SuperAdmin
  useEffect(() => {
    console.log("TimerManagement: FetchTenants useEffect triggered", { userRole });
    
    const fetchTenants = async () => {
      if (userRole !== "SuperAdmin") return;
      
      try {
        setLoading(true);
        const snapshot = await getDocs(collection(firestore, "TenantMaster"));
        const tenants = snapshot.docs.map((doc) => doc.data());
        setTenantList(tenants);
        console.log("TimerManagement: Tenants fetched successfully", tenants.length);
      } catch (error) {
        console.error("Error fetching tenants:", error);
        setErrorMessage("Failed to fetch tenants");
      } finally {
        setLoading(false);
      }
    };

    if (userRole) {
      fetchTenants();
    }
  }, [userRole]);





  // Fetch timer records by multiple document IDs
  const fetchTimerRecordsByIds = async (documentIds) => {
    if (!documentIds.trim() || !selectedTenant) return;
    
    setLoading(true);
    try {
      // Split the input by commas and trim whitespace
      const idArray = documentIds.split(',').map(id => id.trim()).filter(id => id.length > 0);
      
      const records = [];
      const errors = [];
      
      // Fetch each document individually
      for (const docId of idArray) {
        try {
          const docRef = doc(firestore, "Timer", docId);
          const docSnap = await getDoc(docRef);
          
          if (docSnap.exists()) {
            const recordData = docSnap.data();
            
            // Verify the record belongs to the selected tenant
            if (recordData.tenantId === selectedTenant) {
              const record = {
                id: docSnap.id,
                ...recordData,
              };
              records.push(record);
            } else {
              errors.push(`Record ${docId} does not belong to the selected tenant`);
            }
          } else {
            errors.push(`No timer record found with ID: ${docId}`);
          }
        } catch (error) {
          console.error(`Error fetching record ${docId}:`, error);
          errors.push(`Failed to fetch record ${docId}: ${error.message}`);
        }
      }
      
      // Sort records by date (newest first)
      records.sort((a, b) => {
        if (!a.date || !b.date) return 0;
        const dateA = a.date.split('-').reverse().join('-');
        const dateB = b.date.split('-').reverse().join('-');
        return new Date(dateB) - new Date(dateA);
      });
      
      setTimerRecords(records);
      
      // Show success/error messages
      if (records.length > 0 && errors.length === 0) {
        setSuccessMessage(`Successfully fetched ${records.length} timer record(s)`);
        setErrorMessage("");
      } else if (records.length > 0 && errors.length > 0) {
        setSuccessMessage(`Successfully fetched ${records.length} timer record(s)`);
        setErrorMessage(`Some errors occurred: ${errors.join('; ')}`);
      } else if (records.length === 0) {
        setErrorMessage(`No valid records found. Errors: ${errors.join('; ')}`);
      }
      
    } catch (error) {
      console.error("Error fetching timer records:", error);
      setErrorMessage(`Failed to fetch timer records: ${error.message}`);
      setTimerRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTenantSelect = (e) => {
    const selectedTenantId = e.target.value;
    console.log("Tenant selected:", selectedTenantId);
    setSelectedTenant(selectedTenantId);
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setEditForm({
      date: record.date || "",
      startTime: record.startTime || "",
      endTime: record.endTime || "",
      totalTimeBooked: record.totalTimeBooked || "",
      taskDescription: record.taskDescription || record.taskList || "",
    });
    setEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingRecord) return;
    
    try {
      const docRef = doc(firestore, "Timer", editingRecord.id);
      await updateDoc(docRef, {
        date: editForm.date,
        startTime: editForm.startTime,
        endTime: editForm.endTime,
        totalTimeBooked: editForm.totalTimeBooked,
        taskDescription: editForm.taskDescription,
        // Preserve other fields
        userEmail: editingRecord.userEmail,
        userName: editingRecord.userName,
        tenantId: editingRecord.tenantId,
      });

      setSuccessMessage("Timer record updated successfully!");
      setEditDialogOpen(false);
      setEditingRecord(null);
      setEditForm({
        date: "",
        startTime: "",
        endTime: "",
        totalTimeBooked: "",
        taskDescription: "",
      });
      
      // Refresh the current record
      await fetchTimerRecordsByIds(editingRecord.id);
    } catch (error) {
      console.error("Error updating timer record:", error);
      setErrorMessage("Failed to update timer record");
    }
  };

  const handleDelete = async (recordId) => {
    if (!window.confirm("Are you sure you want to delete this timer record?")) return;
    
    try {
      await deleteDoc(doc(firestore, "Timer", recordId));
      setSuccessMessage("Timer record deleted successfully!");
      
      // Clear the records and document ID filter after deletion
      setTimerRecords([]);
      setDocumentIdFilter("");
    } catch (error) {
      console.error("Error deleting timer record:", error);
      setErrorMessage("Failed to delete timer record");
    }
  };

  const handleBulkDelete = async () => {
    if (timerRecords.length === 0) {
      setErrorMessage("No records to delete");
      return;
    }

    const confirmMessage = `Are you sure you want to delete ALL ${timerRecords.length} fetched timer records? This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) return;
    
    setLoading(true);
    try {
      let successCount = 0;
      let errorCount = 0;
      const errors = [];
      
      // Delete each record individually
      for (const record of timerRecords) {
        try {
          await deleteDoc(doc(firestore, "Timer", record.id));
          successCount++;
        } catch (error) {
          console.error(`Error deleting record ${record.id}:`, error);
          errorCount++;
          errors.push(`Failed to delete record ${record.id}: ${error.message}`);
        }
      }
      
      // Show results
      if (successCount === timerRecords.length) {
        setSuccessMessage(`Successfully deleted all ${successCount} timer records!`);
        setErrorMessage("");
      } else if (successCount > 0) {
        setSuccessMessage(`Successfully deleted ${successCount} out of ${timerRecords.length} timer records`);
        setErrorMessage(`Failed to delete ${errorCount} records: ${errors.join('; ')}`);
      } else {
        setErrorMessage(`Failed to delete any records: ${errors.join('; ')}`);
      }
      
      // Clear the records and document ID filter after bulk deletion
      setTimerRecords([]);
      setDocumentIdFilter("");
      
    } catch (error) {
      console.error("Error during bulk delete:", error);
      setErrorMessage(`Bulk delete failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };



  const formatTime = (timeString) => {
    if (!timeString) return "—";
    return timeString;
  };

  const formatTotalTime = (totalTime) => {
    if (!totalTime) return "—";
    if (typeof totalTime === "number") {
      return `${totalTime} mins`;
    }
    return totalTime;
  };

  // Show loading state while context is being initialized
  if (!userRole || userRole === "") {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Stack spacing={2} alignItems="center">
          <CircularProgress size={60} />
          <Typography variant="h6">Loading Timer Management...</Typography>
          <Typography variant="body2" color="textSecondary">
            Please wait while we initialize your session
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (userRole !== "SuperAdmin" && userRole !== "admin") {
    return (
      <Box p={4}>
        <PageNavBar title="Timer Management" backTo="/dashboard" />
        <TenantBanner />
        <Typography variant="h6" color="error">Access Denied</Typography>
        <Typography variant="body1" color="textSecondary">
          You don't have permission to access this page.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", position: "relative" }}>
      <PageNavBar title="Timer Management" backTo="/dashboard" />
      <TenantBanner />
      <Box p={4}>

      <Typography variant="h4" mb={3}>
        Secure Timer Management ({userRole})
      </Typography>
      
      <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
        Access timer records securely by entering the specific document ID. No records are displayed by default.
      </Typography>
      
            {/* <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Security Note:</strong> This interface requires the exact document ID(s) to access timer records. 
          Enter multiple document IDs separated by commas to fetch multiple records at once.
        </Typography>
      </Alert> */}

        {/* Tenant Selector for SuperAdmin */}
        {userRole === "SuperAdmin" && (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Select Tenant</InputLabel>
          <Select
            value={selectedTenant || ""}
            onChange={handleTenantSelect}
            label="Select Tenant"
          >
            {tenantList.map((tenant) => (
              <MenuItem key={tenant.tenantId} value={tenant.tenantId}>
                {tenant.tenantName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Document ID Filter */}
      {selectedTenant ? (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Fetch Timer Records by Document ID(s)
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              label="Enter Document ID(s)"
              value={documentIdFilter}
              onChange={(e) => setDocumentIdFilter(e.target.value)}
              size="small"
              sx={{ minWidth: 400 }}
              placeholder="e.g., user@email.com_1234567890, another@email.com_1234567890"
              helperText="Enter multiple document IDs separated by commas to fetch multiple records"
              multiline
              rows={2}
            />
            <Button
              variant="contained"
              onClick={() => fetchTimerRecordsByIds(documentIdFilter)}
              disabled={!documentIdFilter.trim()}
            >
              Fetch Records
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                setDocumentIdFilter("");
                setTimerRecords([]);
                setErrorMessage("");
                setSuccessMessage("");
              }}
            >
              Clear All
            </Button>
          </Stack>
        </Paper>
      ) : (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Tenant Selection Required
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Please select a tenant to continue. If you're a SuperAdmin, please select a tenant from the dropdown above.
          </Typography>
        </Paper>
      )}

      {/* Success/Error Messages */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage("")}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage("")}>
          {errorMessage}
        </Alert>
      )}

      {/* Timer Records Table */}
      {selectedTenant ? (
        <Paper sx={{ overflowX: "auto" }}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : timerRecords.length === 0 ? (
            <Box p={4} textAlign="center">
              <Typography variant="h6" color="textSecondary">
                Enter document ID(s) above to view timer records.
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Multiple IDs can be entered separated by commas. No records are displayed by default for security reasons.
              </Typography>
            </Box>
          ) : (
            <>
              {/* Bulk Delete Button */}
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', backgroundColor: '#fff3e0' }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleBulkDelete}
                    disabled={loading}
                    size="large"
                  >
                    Delete All {timerRecords.length} Records
                  </Button>
                  {/* <Typography variant="body2" color="error" sx={{ fontWeight: 'medium' }}>
                    ⚠️ This action will permanently delete all {timerRecords.length} fetched timer records and cannot be undone
                  </Typography> */}
                </Stack>
              </Box>
              
              <TableContainer>
                <Table>
                <TableHead>
                <TableRow>
                  {["Date", "User Name", "User Email", "Start Time", "End Time", "Total Time", "Task Description", "Actions"].map(
                    (title) => (
                      <TableCell key={title} sx={{ fontWeight: "bold" }}>
                        {title}
                      </TableCell>
                    )
                  )}
                </TableRow>
              </TableHead>
                  <TableBody>
                    {timerRecords.map((record) => (
                      <TableRow key={record.id}>
                        <TableCell>{record.date || "—"}</TableCell>
                        <TableCell>{record.userName || "—"}</TableCell>
                        <TableCell>{record.userEmail || "—"}</TableCell>
                        <TableCell>{formatTime(record.startTime)}</TableCell>
                        <TableCell>{formatTime(record.endTime)}</TableCell>
                        <TableCell>{formatTotalTime(record.totalTimeBooked)}</TableCell>
                        <TableCell>
                          {record.taskDescription || record.taskList || "—"}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(record)}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(record.id)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Paper>
      ) : null}

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Timer Record</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Date (DD-MM-YY)"
              value={editForm.date}
              onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
              fullWidth
            />
            <TextField
              label="Start Time (HH:MM:SS AM/PM)"
              value={editForm.startTime}
              onChange={(e) => setEditForm({ ...editForm, startTime: e.target.value })}
              fullWidth
            />
            <TextField
              label="End Time (HH:MM:SS AM/PM)"
              value={editForm.endTime}
              onChange={(e) => setEditForm({ ...editForm, endTime: e.target.value })}
              fullWidth
            />
            <TextField
              label="Total Time (minutes)"
              value={editForm.totalTimeBooked}
              onChange={(e) => setEditForm({ ...editForm, totalTimeBooked: e.target.value })}
              fullWidth
            />
            <TextField
              label="Task Description"
              value={editForm.taskDescription}
              onChange={(e) => setEditForm({ ...editForm, taskDescription: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate}>
            Update
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Box>
  );
};

export default TimerManagement;
